import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, ShieldCheck, X } from "lucide-react";
import { api, API } from "../lib/api";
import { fmtAxv } from "../lib/tokens";
import { useWorld } from "../state/World";

/**
 * The Proof.
 *
 * Everything else in the World asks to be believed. This page asks to be
 * checked. Three claims, each with a button that runs against the real
 * database in front of the person reading it:
 *
 *   1. the ledger conserves — replayed live, every entry ever written
 *   2. a crossing carries identity to another origin, sub-unit exact
 *   3. a spent crossing is refused, and the refusal comes from the datastore
 *
 * Nothing here is precomputed and nothing is mocked. If it ever goes red in
 * front of a judge, it is because it is genuinely broken — which is the point.
 */
const Row = ({ label, value, ok }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-white/6 py-2 last:border-0">
    <span className="mono text-[10px] uppercase tracking-[0.16em] text-white/35">{label}</span>
    <span
      className={`mono text-[12px] ${
        ok === false ? "text-red-400" : ok ? "text-[var(--stage-gold)]" : "text-white/70"
      }`}
    >
      {value}
    </span>
  </div>
);

const Panel = ({ n, title, claim, children, testid }) => (
  <motion.section
    className="glass-warm rounded-2xl p-6 md:p-8"
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: n * 0.08 }}
    data-testid={testid}
  >
    <p className="mono mb-2 text-[9px] tracking-[0.22em] text-[var(--stage-gold)]/70">
      CLAIM {n}
    </p>
    <h2 className="serif text-2xl leading-snug text-[#f7ecd8] md:text-3xl">{title}</h2>
    <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-white/50">{claim}</p>
    <div className="mt-6">{children}</div>
  </motion.section>
);

export default function Proof() {
  const { me, portals, portal } = useWorld();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [cross, setCross] = useState(null);
  const [busy, setBusy] = useState(null);

  const here = typeof window === "undefined" ? "" : window.location.origin;
  const thisPortal = portals.find((p) => p.key === portal);
  const others = portals.filter((p) => p.origin && p.key !== portal && p.live !== false);

  const runReconcile = async () => {
    setBusy("rec");
    try {
      const { data } = await api.get("/ledger/reconcile");
      setRec(data);
    } catch (e) {
      setRec({ error: e?.response?.data?.detail || "could not reach the ledger" });
    } finally {
      setBusy(null);
    }
  };

  // Mint -> redeem -> replay, without disturbing the session in this tab. The
  // redeemed token is deliberately NOT stored: we only use it to prove it
  // resolves to the same human with the same Pouch.
  const runCrossing = async () => {
    setBusy("cross");
    const out = { steps: [] };
    try {
      const target = others[0] || thisPortal;
      const mint = await api.post("/seam/cross", { to: target.key });
      const token = mint.data.crossing_token;
      out.destination = mint.data.origin;
      out.expires = mint.data.expires_in;
      out.jti = `${token.slice(-12)}`;

      const before = await api.get("/me");
      out.beforeUser = before.data.user.id;
      out.beforePouch = before.data.pouch_sub;

      const red = await axios.post(`${API}/auth/cross`, {
        crossing_token: token,
        portal: target.key,
      });
      const after = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${red.data.token}` },
      });
      out.afterUser = after.data.user.id;
      out.afterPouch = after.data.pouch_sub;
      out.same = out.beforeUser === out.afterUser && out.beforePouch === out.afterPouch;

      try {
        await axios.post(`${API}/auth/cross`, {
          crossing_token: token,
          portal: target.key,
        });
        out.replay = "ACCEPTED — this is a failure";
        out.replayOk = false;
      } catch (e) {
        out.replay = e?.response?.data?.detail || "refused";
        out.replayOk = e?.response?.status === 409 || /already used/i.test(out.replay);
      }
    } catch (e) {
      out.error = e?.response?.data?.detail || "the crossing could not be minted";
    }
    setCross(out);
    setBusy(null);
  };

  return (
    <div className="min-h-screen bg-[var(--ink)] pb-40">
      <div className="mx-auto max-w-3xl px-5 pt-8 md:px-8">
        <button
          type="button"
          onClick={() => nav("/")}
          data-testid="proof-back"
          className="mono mb-8 flex items-center gap-2 text-[10px] text-white/35 transition-colors hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> back to the World
        </button>

        <header className="mb-10">
          <p className="tracked mb-2 flex items-center gap-2 text-[9px] text-[var(--stage-gold)]/70">
            <ShieldCheck size={11} /> THE PROOF
          </p>
          <h1 className="serif text-4xl font-light leading-[1.05] text-[#f7ecd8] sm:text-5xl">
            Do not take our word for it.
            <span className="italic text-[var(--stage-gold)]"> Press the buttons.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/55">
            Three claims this World makes. Each one runs live against the real
            database when you click it. Nothing on this page is precomputed, and
            nothing is mocked — if it goes red, it is genuinely broken.
          </p>
        </header>

        <div className="space-y-6">
          <Panel
            n={1}
            testid="proof-where"
            title="One substrate, three front doors."
            claim="Three separate domains, three separate origins, one database. You are reading this from one of them right now."
          >
            <div data-testid="proof-origin-rows">
              <Row label="this origin" value={here} />
              <Row label="serving" value={thisPortal?.name || "the World"} />
              <Row
                label="you, here"
                value={me ? `${me.user.id.slice(-8)} · ${fmtAxv(me.pouch_sub)} AXV` : "—"}
              />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              {others.map((p) => (
                <a
                  key={p.key}
                  href={`${p.origin}/proof`}
                  className="btn"
                  data-testid={`proof-open-${p.key}`}
                >
                  Open this page on {p.origin.replace("https://", "")} →
                </a>
              ))}
            </div>
            <p className="mono mt-3 text-[10px] leading-relaxed text-white/30">
              Open one. Different domain, different origin — and the number above
              will be the same, because there is only one of you.
            </p>
          </Panel>

          <Panel
            n={2}
            testid="proof-ledger"
            title="The ledger conserves. Every entry ever written."
            claim="Double-entry, append-only, integer sub-units. Balances are derived, never stored. This replays the entire entry stream and proves credits equal debits."
          >
            <button
              type="button"
              className="btn btn-gold"
              disabled={busy === "rec"}
              onClick={runReconcile}
              data-testid="run-reconcile-button"
            >
              {busy === "rec" ? (
                <Loader2 className="mx-auto animate-spin" size={14} />
              ) : (
                "Reconcile the ledger now"
              )}
            </button>

            {rec && !rec.error && (
              <motion.div
                className="mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="reconcile-result"
              >
                <Row label="transactions replayed" value={rec.transactions} />
                <Row label="total credits" value={`${fmtAxv(rec.total_credits_sub)} AXV`} />
                <Row label="total debits" value={`${fmtAxv(rec.total_debits_sub)} AXV`} />
                <Row
                  label="conserved"
                  ok={rec.conserved}
                  value={rec.conserved ? "TRUE — credits = debits" : "FALSE"}
                />
                <div
                  className={`mt-4 flex items-center gap-2 ${
                    rec.conserved ? "text-[var(--stage-gold)]" : "text-red-400"
                  }`}
                >
                  {rec.conserved ? <Check size={14} /> : <X size={14} />}
                  <span className="mono text-[10px] uppercase tracking-[0.18em]">
                    {rec.conserved ? "the books balance" : "the books do not balance"}
                  </span>
                </div>
              </motion.div>
            )}
            {rec?.error && (
              <p className="mono mt-4 text-[11px] text-red-400">{rec.error}</p>
            )}
          </Panel>

          <Panel
            n={3}
            testid="proof-crossing"
            title="A crossing carries you. Once, and only once."
            claim="Identity moves between origins on a signed, single-use token that expires in two minutes. This mints one, spends it, then tries to spend it again in front of you."
          >
            <button
              type="button"
              className="btn btn-gold"
              disabled={busy === "cross" || !me}
              onClick={runCrossing}
              data-testid="run-crossing-button"
            >
              {busy === "cross" ? (
                <Loader2 className="mx-auto animate-spin" size={14} />
              ) : (
                "Mint a crossing, then burn it"
              )}
            </button>

            {cross && !cross.error && (
              <motion.div
                className="mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="crossing-result"
              >
                <Row label="minted for" value={cross.destination} />
                <Row label="expires in" value={`${cross.expires}s`} />
                <Row label="token tail" value={cross.jti} />
                <Row label="you, before" value={`${cross.beforeUser?.slice(-8)} · ${fmtAxv(cross.beforePouch)}`} />
                <Row label="you, after" value={`${cross.afterUser?.slice(-8)} · ${fmtAxv(cross.afterPouch)}`} />
                <Row
                  label="same human, same pouch"
                  ok={cross.same}
                  value={cross.same ? "TRUE — identical to the sub-unit" : "FALSE"}
                />
                <Row
                  label="replay of the spent token"
                  ok={cross.replayOk}
                  value={cross.replay}
                />
                <div
                  className={`mt-4 flex items-center gap-2 ${
                    cross.same && cross.replayOk ? "text-[var(--stage-gold)]" : "text-red-400"
                  }`}
                >
                  {cross.same && cross.replayOk ? <Check size={14} /> : <X size={14} />}
                  <span className="mono text-[10px] uppercase tracking-[0.18em]">
                    {cross.same && cross.replayOk
                      ? "carried once, refused twice"
                      : "the seam did not behave"}
                  </span>
                </div>
                <p className="mono mt-4 text-[10px] leading-relaxed text-white/30">
                  The refusal is not application logic being polite. The token id
                  is written into a uniqueness-constrained collection, so the
                  second attempt is rejected by the database itself.
                </p>
              </motion.div>
            )}
            {cross?.error && (
              <p className="mono mt-4 text-[11px] text-red-400">{cross.error}</p>
            )}
          </Panel>
        </div>

        <p className="mono mt-10 text-[10px] leading-relaxed text-white/25">
          Alpha. Payments are mocked and clearly labelled as such. Everything on
          this page is not.
        </p>
      </div>
    </div>
  );
}
